import { create } from 'zustand';

export type MaterialsPanelMode = 'select' | 'manage';

/** onSelect に渡す選択素材の要約 (案件画像の差替で SVG 原本を引き継ぐための最小情報) */
export type SelectedPanelMaterial = {
  id: string;
  folderType: 'temporary' | 'cloud';
  contentType: string | null;
};

interface MaterialsPanelContextData {
  targetAsin?: string;  // Product側で画像差替時に使用
  targetBrandId?: string;  // ブランドIDでフィルタリング
  targetSlot?: string;  // 画像スロット情報（main, pt01, etc）
  /**
   * 画像選択時のコールバック。imageUrl はラスタ画像の URL (非ラスタ原本はラスタ版)。
   * material は選んだ素材 (非ラスタ原本 = SVG を案件画像に置くとき、原本を引き継ぐために使う)
   */
  onSelect?: (imageUrl: string, material?: SelectedPanelMaterial) => void;
}

interface MaterialsPanelState {
  isOpen: boolean;
  mode: MaterialsPanelMode;
  contextData?: MaterialsPanelContextData;
  /** 素材詳細モーダルを開くリクエスト（素材ID） */
  requestDetailMaterialId: string | null;
  /** 商品紐づけボタンにコーチマークを表示するか */
  showProductLinkCoachMark: boolean;
  /** 新着素材があるか（フローティングボタンの通知ドット用） */
  hasNewMaterials: boolean;
  /** 外部から強制するアスペクト比フィルタ（nullの場合は強制なし） */
  forcedAspectRatios: string[] | null;
  /** 外部から強制する種別フィルタ（'image' | 'video' | 'audio'、nullの場合は強制なし） */
  forcedFileTypes: string[] | null;
  /** 外部から強制するタグフィルタ（タグID）。タブ切替も伴う */
  forcedTagFilter: { tagId: string; folder: 'shared' | 'personal' } | null;
  /** 外部から強制するプロダクトフィルタ（`<type>:<id>`）。タブ切替も伴う */
  forcedProductFilter: { productFilter: string; folder: 'shared' | 'personal' } | null;
  /** パネル上部に一時表示するインフォメッセージ */
  panelInfoMessage: string | null;
  /** ストーリーボードタブで表示する絵コンテ画像 URL。
   *  非 null の間だけ「ストーリーボード」タブが表示される (3rd タブ)。
   *  null になると素材一覧 (cloud/temporary) のみのタブ構成に戻る。 */
  storyboardImageUrl: string | null;
  /** ストーリーボードタブ表示時のラベル文字 (例: 'A')。
   *  タブ自身の文字列は「ストーリーボード」で固定し、ラベルはコンテンツ
   *  エリア側の見出しと送り/戻すナビに使う。 */
  storyboardLabel: string | null;
  /** ストーリーボードタブで表示中の絵コンテが属するシナリオ名。
   *  ダウンロード時のファイル名 ({シナリオ名}_{A,B,...}.png) に使う。 */
  storyboardScenarioTitle: string | null;
  /** 送り/戻すボタンで遷移できるストーリーボード候補一覧。
   *  StoryboardCandidates 側で setStoryboardItems を呼んで都度反映する。 */
  storyboardItems: Array<{ id: string; url: string; label: string; materialId?: string | null }>;
  /** タブを切替えたいときの「リクエスト」。RightPanelWrapper / SimpleMaterialsList が
   *  これを拾って activeTab を切り替えたら clearRequestActiveTab で null に戻す。
   *  既存の shouldOpenPersonalFolder / forcedTagFilter と同じパターン。 */
  requestActiveTab: 'cloud' | 'temporary' | 'storyboard' | null;

  // Actions
  open: (mode: MaterialsPanelMode, contextData?: MaterialsPanelContextData) => void;
  close: () => void;
  setMode: (mode: MaterialsPanelMode) => void;
  /** 素材詳細モーダルを開く（パネルも自動で開く） */
  openMaterialDetail: (materialId: string) => void;
  clearDetailRequest: () => void;
  dismissProductLinkCoachMark: () => void;
  /** 新着素材の通知フラグをONにする */
  notifyNewMaterial: () => void;
  /** アスペクト比フィルタを強制する */
  setForcedAspectRatios: (ratios: string[] | null) => void;
  /** 種別フィルタを強制する */
  setForcedFileTypes: (types: string[] | null) => void;
  /** タグフィルタを強制する。同時にフォルダタブも切り替える */
  openWithTagFilter: (tagId: string, folder: 'shared' | 'personal') => void;
  /** 強制タグフィルタをクリア */
  clearForcedTagFilter: () => void;
  /** プロダクトフィルタを強制する。同時にフォルダタブも切り替える */
  openWithProductFilter: (productFilter: string, folder: 'shared' | 'personal') => void;
  /** 強制プロダクトフィルタをクリア */
  clearForcedProductFilter: () => void;
  /** インフォメッセージを設定 */
  setPanelInfoMessage: (message: string | null) => void;
  /** 絵コンテ拡大表示モードでパネルを開く。
   *  storyboardImageUrl を立てて「ストーリーボード」タブを 3rd タブとして
   *  出現させ、requestActiveTab='storyboard' で自動的にそのタブに切り替える。
   *  label を渡すとタブ表記に付加される（例: 'A' → 'ストーリーボードA'）。
   *  scenarioTitle はダウンロード時のファイル名に使う。 */
  openStoryboardPreview: (imageUrl: string, label?: string | null, scenarioTitle?: string | null) => void;
  /** ストーリーボードタブを閉じる (3rd タブを消す)。素材タブのみに戻る。 */
  closeStoryboardPreview: () => void;
  /** タブ切替リクエストをクリアする (RightPanelWrapper が消化後に呼ぶ)。 */
  clearRequestActiveTab: () => void;
  /** 候補一覧を更新する。StoryboardCandidates が状態変更のたびに呼ぶ。 */
  setStoryboardItems: (items: Array<{ id: string; url: string; label: string; materialId?: string | null }>) => void;
  /** 現在表示中の絵コンテから delta だけシフトして別の候補を表示する。
   *  端を超えるとクランプ (折り返さない)。items 内に現在画像が無ければ
   *  先頭/末尾にジャンプする。 */
  selectStoryboardByOffset: (delta: number) => void;
}

// 右パネルに表示するストーリーボードのラベル整形。レター("A"/"B"...) に「案」を
// 付けて「案A」「案B」にする。カード上のレターバッジ等とは別経路でここだけ装飾する。
const formatStoryboardLabel = (label?: string | null): string | null =>
  label ? `案${label}` : null;

export const useMaterialsPanelStore = create<MaterialsPanelState>((set) => ({
  isOpen: false,
  // 既定は読み書き可能な管理モード。素材選択フロー (商品画像差し替え等) は
  // 呼び出し側が open('select', ...) を明示する。
  mode: 'manage',
  contextData: undefined,
  requestDetailMaterialId: null,
  showProductLinkCoachMark: false,
  hasNewMaterials: false,
  forcedAspectRatios: null,
  forcedFileTypes: null,
  forcedTagFilter: null,
  forcedProductFilter: null,
  panelInfoMessage: null,
  storyboardImageUrl: null,
  storyboardLabel: null,
  storyboardScenarioTitle: null,
  storyboardItems: [],
  requestActiveTab: null,

  open: (mode, contextData) => set({ isOpen: true, mode, contextData, hasNewMaterials: false }),
  close: () => set({ isOpen: false, contextData: undefined, forcedAspectRatios: null, forcedFileTypes: null, forcedTagFilter: null, forcedProductFilter: null, panelInfoMessage: null, storyboardImageUrl: null, storyboardLabel: null, storyboardScenarioTitle: null, storyboardItems: [], requestActiveTab: null }),
  setMode: (mode) => set({ mode }),
  openMaterialDetail: (materialId) => set({ isOpen: true, requestDetailMaterialId: materialId, showProductLinkCoachMark: true }),
  clearDetailRequest: () => set({ requestDetailMaterialId: null }),
  dismissProductLinkCoachMark: () => set({ showProductLinkCoachMark: false }),
  notifyNewMaterial: () => set((state) => state.isOpen ? {} : { hasNewMaterials: true }),
  setForcedAspectRatios: (ratios) => set({ forcedAspectRatios: ratios }),
  setForcedFileTypes: (types) => set({ forcedFileTypes: types }),
  openWithTagFilter: (tagId, folder) =>
    set({ isOpen: true, mode: 'manage', forcedTagFilter: { tagId, folder }, hasNewMaterials: false }),
  clearForcedTagFilter: () => set({ forcedTagFilter: null }),
  openWithProductFilter: (productFilter, folder) =>
    set({ isOpen: true, mode: 'manage', forcedProductFilter: { productFilter, folder }, hasNewMaterials: false }),
  clearForcedProductFilter: () => set({ forcedProductFilter: null }),
  setPanelInfoMessage: (message) => set({ panelInfoMessage: message }),
  openStoryboardPreview: (imageUrl, label, scenarioTitle) => set({
    isOpen: true,
    // 素材フォルダは読み書き可能な通常モードで開く (select だと Amazon 推奨規定
    // フィルタが既定 ON になり、共有/個人フォルダの閲覧が絞り込まれてしまう)。
    mode: 'manage',
    storyboardImageUrl: imageUrl,
    storyboardLabel: formatStoryboardLabel(label),
    storyboardScenarioTitle: scenarioTitle ?? null,
    requestActiveTab: 'storyboard',
  }),
  closeStoryboardPreview: () => set({
    storyboardImageUrl: null,
    storyboardLabel: null,
    storyboardScenarioTitle: null,
    storyboardItems: [],
    // 素材タブに戻す。既に他のタブに居る場合は触らない。
    requestActiveTab: 'cloud',
  }),
  clearRequestActiveTab: () => set({ requestActiveTab: null }),
  setStoryboardItems: (items) => set((state) => {
    // 等価ガード: 中身が同じなら state を据え置いて再レンダーを発火させない。
    // (呼び出し側の effect が毎レンダー新しい配列で呼んでも無限ループにならないようにする)
    const prev = state.storyboardItems;
    const same = prev.length === items.length && prev.every((p, i) =>
      p.id === items[i].id && p.url === items[i].url && p.label === items[i].label);
    return same ? state : { storyboardItems: items };
  }),
  selectStoryboardByOffset: (delta) => set((state) => {
    const items = state.storyboardItems;
    if (!items.length) return {};
    const currentIdx = items.findIndex((it) => it.url === state.storyboardImageUrl);
    // 現在画像が一覧に無ければ delta>0 で先頭、delta<0 で末尾を出す
    const base = currentIdx >= 0 ? currentIdx : (delta > 0 ? -1 : items.length);
    const nextIdx = Math.max(0, Math.min(items.length - 1, base + delta));
    if (nextIdx === currentIdx) return {};
    const next = items[nextIdx];
    return { storyboardImageUrl: next.url, storyboardLabel: formatStoryboardLabel(next.label) };
  }),
}));
